// src/components/OfflineBanner.jsx
// ══════════════════════════════════════════════════════════════════════════════
// KAILUX — OFFLINE / AI UNAVAILABILITY BANNER  (Phase A1)
// ══════════════════════════════════════════════════════════════════════════════
// Shown in three states:
//   1. OFFLINE     — no network at all (navigator.onLine = false)
//   2. AI_DOWN     — network OK, but KaiLux AI circuit is open (rule-engine active)
//   3. DEGRADED    — API reachable but health check shows non-200
// Dismissed per-session with a close button.
// Automatically hides when all systems recover.
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from 'react'

import Icon from './Icon'
import { T, tint } from '../theme/theme'
const API_URL = import.meta.env.VITE_API_URL || 'https://kailux-platform-production.up.railway.app'
const CHECK_INTERVAL_MS   = 30_000   // 30 s between health checks
const RETRY_INTERVAL_MS   = 10_000   // 10 s when known-down (faster recovery detection)

const STATE = {
  OK:       'ok',
  OFFLINE:  'offline',
  AI_DOWN:  'ai_down',
  DEGRADED: 'degraded',
}

const BANNER_CONFIG = {
  [STATE.OFFLINE]: {
    bg:    tint(T.rd, 10),
    border:tint(T.rd, 35),
    icon:  <Icon name="offline" />,
    text:  'No internet connection — KaiLux is running in offline mode. Your data is safe. All features will resume when you reconnect.',
    badge: { label: 'OFFLINE', bg: tint(T.rd, 16), color: T.rd },
    color: T.rd,
  },
  [STATE.AI_DOWN]: {
    bg:    tint(T.am, 10),
    border:tint(T.am, 35),
    icon:  <Icon name="refresh" />,
    text:  'AI engine temporarily unavailable — KaiLux is using its built-in rule engine. Account mapping, note templates, and observations all continue to work automatically.',
    badge: { label: 'RULE ENGINE ACTIVE', bg: tint(T.am, 16), color: T.am },
    color: T.am,
  },
  [STATE.DEGRADED]: {
    bg:    tint(T.bl, 10),
    border:tint(T.bl, 35),
    icon:  <Icon name="alert" />,
    text:  'KaiLux is experiencing intermittent connectivity. Some features may be slower than usual. We are working to restore full service.',
    badge: { label: 'DEGRADED', bg: tint(T.bl, 16), color: T.bl },
    color: T.bl,
  },
}

export function useConnectivityState() {
  const [state, setState]           = useState(STATE.OK)
  const [lastChecked, setLastChecked] = useState(null)
  const intervalRef = useRef(null)

  const check = useCallback(async () => {
    // 1. Check browser online status first
    if (!navigator.onLine) {
      setState(STATE.OFFLINE)
      setLastChecked(new Date())
      return
    }

    // 2. Hit the /health endpoint
    try {
      const controller = new AbortController()
      const timeout    = setTimeout(() => controller.abort(), 8000)
      const res        = await fetch(`${API_URL}/health`, {
        signal: controller.signal,
        cache:  'no-store',
      })
      clearTimeout(timeout)

      if (!res.ok) {
        setState(STATE.DEGRADED)
        setLastChecked(new Date())
        return
      }

      const data = await res.json().catch(() => ({}))

      // 3. Check circuit breaker state in health payload
      if (data.ai_available === false) {
        setState(STATE.AI_DOWN)
      } else {
        setState(STATE.OK)
      }
      setLastChecked(new Date())

    } catch {
      // Network error or timeout — treat as degraded (network might still work for DB)
      if (!navigator.onLine) {
        setState(STATE.OFFLINE)
      } else {
        setState(STATE.DEGRADED)
      }
      setLastChecked(new Date())
    }
  }, [])

  useEffect(() => {
    check()

    const getInterval = () =>
      state === STATE.OK ? CHECK_INTERVAL_MS : RETRY_INTERVAL_MS

    intervalRef.current = setInterval(() => {
      check()
    }, getInterval())

    // Also re-check on browser online/offline events
    const handleOnline  = () => check()
    const handleOffline = () => setState(STATE.OFFLINE)
    window.addEventListener('online',  handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      clearInterval(intervalRef.current)
      window.removeEventListener('online',  handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [check, state])

  return { state, lastChecked, recheck: check }
}


export default function OfflineBanner() {
  const { state, lastChecked, recheck } = useConnectivityState()
  const [dismissed, setDismissed]       = useState(false)
  const [retrying,  setRetrying]        = useState(false)

  // Re-show banner if state worsens after dismissal
  useEffect(() => {
    if (state !== STATE.OK) setDismissed(false)
  }, [state])

  if (state === STATE.OK || dismissed) return null

  const cfg = BANNER_CONFIG[state]

  const handleRetry = async () => {
    setRetrying(true)
    await recheck()
    setRetrying(false)
  }

  const fmt = (d) => d
    ? d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—'

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        position:        'fixed',
        top:             0,
        left:            0,
        right:           0,
        zIndex:          9999,
        background:      cfg.bg,
        borderBottom:    `1px solid ${cfg.border}`,
        padding:         '10px 20px',
        display:         'flex',
        alignItems:      'center',
        gap:'12px',
        fontFamily:      "'Outfit', sans-serif",
        fontSize:'14px',
        boxShadow:       T.sh,
        animation:       'bannerSlideDown 0.3s ease',
      }}
    >
      {/* Badge */}
      <span style={{
        flexShrink:    0,
        fontSize:      '9px',
        fontWeight:    '800',
        padding:       '3px 8px',
        borderRadius:'6px',
        letterSpacing: '1.5px',
        background:    cfg.badge.bg,
        color:         cfg.badge.color,
        whiteSpace:    'nowrap',
      }}>
        {cfg.icon} {cfg.badge.label}
      </span>

      {/* Message */}
      <span style={{ flex: 1, color: cfg.color, lineHeight: '1.5' }}>
        {cfg.text}
        {lastChecked && (
          <span style={{ marginLeft: '8px', opacity: 0.6, fontSize:'12px' }}>
            Last checked: {fmt(lastChecked)}
          </span>
        )}
      </span>

      {/* Actions */}
      <div style={{ display: 'flex', gap:'8px', flexShrink: 0 }}>
        <button
          onClick={handleRetry}
          disabled={retrying}
          style={{
            padding:       '5px 14px',
            borderRadius:'6px',
            border:        `1px solid ${cfg.border}`,
            background:    'transparent',
            color:         cfg.color,
            fontFamily:    "'Outfit', sans-serif",
            fontSize:'13px',
            fontWeight:    '600',
            cursor:        retrying ? 'not-allowed' : 'pointer',
            opacity:       retrying ? 0.5 : 1,
            transition:'opacity 0.2s',
          }}
        >
          {retrying ? 'Checking...' : '↻ Retry'}
        </button>

        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          style={{
            padding:      '5px 10px',
            borderRadius:'6px',
            border:       `1px solid ${cfg.border}`,
            background:   'transparent',
            color:        cfg.color,
            fontFamily:   "'Outfit', sans-serif",
            fontSize:     '14px',
            cursor:       'pointer',
            opacity:      0.7,
            lineHeight:   1,
          }}
        >
          ×
        </button>
      </div>

      <style>{`
        @keyframes bannerSlideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
      `}</style>
    </div>
  )
}
